/* eslint-disable max-len */
import * as React from 'react';
import { reaction, IReactionDisposer } from 'mobx';
import { Progress, ButtonGroup, Button, Collapse, Alert, ListGroupItem } from 'reactstrap';
import { observer } from 'mobx-react';

import { DateTimeService } from '@app/Services/DateTimeService';
import ReactService from '@app/Services/ReactService';
import { ApiV1Job, JobStatus, QueueModel } from '@app/Models/WebApiModels';

import { ToolTipItem } from '@app/Components/ToolTipItem';
import { Icon } from '@app/Components/Icon';
import Status from '@app/Components/Status/Status';
import { SelfTestQueue } from '@app/Pages/HealthBoard/Components/SelfTestQueue/SelfTestQueue';
import { getJobErrorMessage, isTriggerFailed, TriggerExtended } from '../../Models/JobListModels';
import JobItemStore from './JobItemStore';
import './job-item.scss';

const LINK_REGEX = /({site-root}\/[a-z-]+)/gi;
const COLLAPSED_TRIGGERS_COUNT = 5;

export type JobItemProps = {
    job: ApiV1Job;
    queues: QueueModel[];
    hasReadAccess?: boolean;
    hasWriteAccess?: boolean;
    hasRunAccess?: boolean;
    renderText?: (text: string) => JSX.Element;
    onParametersSaved?: (e: ApiV1Job) => void;
    onSettingsSaved?: (e: ApiV1Job) => void;
    onTriggerCreated?: (e: ApiV1Job) => void;
    onTriggerDeleted?: (e: ApiV1Job) => void;
    onJobStarted?: (e: ApiV1Job) => void;
};

@observer
export default class JobItem extends React.Component<JobItemProps> {
    private _store: JobItemStore;
    private readonly _statusReactionDisposer: IReactionDisposer;

    constructor(props: JobItemProps) {
        super(props);
        this._store = new JobItemStore(props);

        this._statusReactionDisposer = reaction(
            () => this.props.job.lastStatus,
            (st) => {
                if (st === JobStatus.Fail || st === JobStatus.Success) {
                    this._store.setIsStopping(false);
                }
            }
        );
    }

    componentWillUnmount() {
        this._statusReactionDisposer();
    }

    private get _triggers(): TriggerExtended[] {
        return this.props.job.triggers ?? [];
    }

    public render() {
        const { job: { name = '', description }, renderText } = this.props;
        const isLink = description && new RegExp(LINK_REGEX).test(description);

        return (
            <ListGroupItem className="job-list-item">
                <div className="job-item-header">
                    <div className="job-item-info">
                        <div className="job-item-title">
                            {this._renderActiveStatus()}
                            <span className="job-name">{renderText ? renderText(name) : name}</span>
                            {this._renderInstance()}
                            {this.renderResultStatus()}
                            {this.renderInfoMessage()}
                            {name === 'QueueProcessJob' && this._renderQueues()}
                        </div>
                        {description && (
                            <span className="job-item-description">
                                {isLink
                                    ? this.renderLink(description)
                                    : renderText ? renderText(description) : description
                                }
                            </span>
                        )}
                    </div>
                    {this.renderActions()}
                </div>
                {this.renderTriggerProgress()}
                {this.renderTriggerError()}
            </ListGroupItem>
        );
    }

    private _renderActiveStatus() {
        const { job } = this.props;

        const activeTriggers = this._triggers.filter(tr => tr.nextRunDateTime && tr.isActive);
        const trigger = activeTriggers.length ? activeTriggers[activeTriggers.length - 1] : null;
        const elementId = (trigger ? trigger.name : job.id) ?? '';
        const color = trigger && trigger.isActive ? 'success' : 'secondary';

        return (
            <>
                <Status id={elementId} color={color} pill />
                {trigger && (
                    <ToolTipItem targetId={elementId} className="tooltip-max-content">
                        <div className="job-active-triggers">
                            {activeTriggers.map((t) => (
                                <div key={t.name}>
                                    {t.name && <div>{t.name}</div>}
                                    {t.cronSchedule && <div>Cron: {t.cronSchedule}</div>}
                                    {t.nextRunDateTime && <div>Next: {DateTimeService.toUiDateTime(t.nextRunDateTime)}</div>}
                                </div>
                            ))}
                        </div>
                    </ToolTipItem>
                )}
            </>
        );
    }

    private _renderInstance() {
        const { instance } = this.props.job;
        if (instance !== 'VM' && instance !== 'Azure') return null;

        return (
            <span className="job-instance" title={instance}>
                {instance === 'VM' && <svg fill="currentColor" width="24px" height="24px" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11,5H25V8h2V5a2,2,0,0,0-2-2H11A2,2,0,0,0,9,5v6.85h2Z"></path>
                    <path d="M30,10H17v2h8v6h2V12h3V26H22V17a2,2,0,0,0-2-2H6a2,2,0,0,0-2,2V31a2,2,0,0,0,2,2H20a2,2,0,0,0,2-2V28h8a2,2,0,0,0,2-2V12A2,2,0,0,0,30,10ZM6,31V17H20v9H16V20H14v6a2,2,0,0,0,2,2h4v3Z"></path>
                </svg>}
                {instance === 'Azure' && <svg fill="currentColor" width="24px" height="24px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.3702 13.6799L11.3702 1.67989C11.3006 1.47291 11.1652 1.29438 10.9846 1.17159C10.804 1.0488 10.5882 0.988513 10.3702 0.999896H5.63017C5.42052 0.999354 5.21598 1.0647 5.04551 1.18672C4.87504 1.30875 4.74724 1.48127 4.68015 1.67989L0.630165 13.6799C0.577646 13.8346 0.56382 13.9998 0.589943 14.1611C0.616066 14.3225 0.681335 14.4749 0.780007 14.6052C0.878678 14.7354 1.00778 14.8395 1.15598 14.9083C1.30419 14.9771 1.46699 15.0086 1.63017 14.9999H4.56016C4.76809 14.9984 4.97035 14.932 5.13883 14.8101C5.30731 14.6883 5.43363 14.5169 5.50016 14.3199L6.11015 12.5399L9.11015 14.8099C9.28448 14.9362 9.49495 15.0028 9.71018 14.9999H14.3902C14.5517 15.0052 14.7121 14.9712 14.8576 14.901C15.0032 14.8307 15.1295 14.7263 15.2259 14.5965C15.3222 14.4668 15.3856 14.3156 15.4107 14.156C15.4359 13.9963 15.422 13.833 15.3702 13.6799ZM9.75016 14.3399C9.67748 14.3399 9.60693 14.3153 9.55015 14.2699L3.90018 10.0799L3.81016 10.0099H6.81016L6.89017 9.79988L7.89017 7.26988L10.1302 13.8999C10.1482 13.9555 10.1515 14.0148 10.1399 14.072C10.1283 14.1293 10.1022 14.1826 10.064 14.2269C10.0258 14.2711 9.97689 14.3047 9.92191 14.3245C9.86694 14.3443 9.80778 14.3496 9.75016 14.3399V14.3399ZM14.4201 14.3399H10.7002C10.7749 14.1262 10.7749 13.8935 10.7002 13.6799L6.65018 1.67989H10.3702C10.4408 1.68024 10.5095 1.70258 10.5669 1.74379C10.6242 1.78501 10.6673 1.84308 10.6902 1.9099L14.7402 13.9099C14.7538 13.9597 14.756 14.012 14.7464 14.0628C14.7369 14.1136 14.7159 14.1615 14.6851 14.203C14.6542 14.2444 14.6144 14.2783 14.5685 14.302C14.5226 14.3257 14.4718 14.3387 14.4201 14.3399V14.3399Z"/>
                </svg>}
            </span>
        );
    }

    renderResultStatus() {
        const { job } = this.props;
        const pendingTriggers = this._triggers
            .filter(tr => tr.status === JobStatus.Pending)
            .map(tr => tr.name)
            .join(', ');

        const elementId = job.id + '_error_message';

        let color = 'secondary';
        switch (job.lastStatus) {
            case JobStatus.Fail:
                color = 'danger';
                break;
            case JobStatus.Success:
                color = 'success';
                break;
            case JobStatus.Pending:
                color = 'secondary';
                break;
        }

        const failedSchedules = this._triggers.filter(t => !!t.scheduleError);

        let jobStatus: JobStatus | undefined = job.lastStatus;
        if (failedSchedules.length) {
            color = 'danger';
            jobStatus = JobStatus.Fail;
        }

        if (!jobStatus) return '';

        return (
            <Status id={elementId} color={color} onClick={this.toggleLastError}>
                {jobStatus === JobStatus.Pending && <ToolTipItem text={pendingTriggers} targetId={elementId} />}
                {jobStatus}
            </Status>
        );
    }

    renderInfoMessage() {
        const { job: { id, lastExecutionInfoMessage } } = this.props;
        const elementId = id + '_info_message';

        const failedSchedules = this._triggers.filter(t => !!t.scheduleError);

        if (!lastExecutionInfoMessage && !failedSchedules.length) return '';

        const messages = (
            <>
                {lastExecutionInfoMessage && lastExecutionInfoMessage.split('\n').map((m, i) => <div className="my-2" key={'info_message_' + i}>{m}</div>)}
                {lastExecutionInfoMessage ? '.' : ''}
                {failedSchedules.map((s, i) => <React.Fragment key={'schedule_error_' + i}>{i ? ', ' : ''}{s.scheduleError}</React.Fragment>)}
            </>
        );

        return (
            <>
                <ToolTipItem text={messages} targetId={elementId} className="tooltip-wide" />
                <span id={elementId} className="job-info-icon text-info">
                    <Icon name="info-circle" />
                </span>
            </>
        );
    }

    renderActions() {
        const { job: { name }, hasReadAccess, hasWriteAccess, hasRunAccess } = this.props;
        const isRunning = this._triggers.find(tr => tr.status === this._store.statusPending);

        return (
            <ButtonGroup size="sm">
                <Button outline size="sm" title={'New trigger for ' + name} onClick={this.showNewTriggerJobParams} disabled={!hasWriteAccess}>
                    <Icon name="plus" />
                </Button>
                <Button outline size="sm" title={'Settings for ' + name} onClick={this.showJobSettings} disabled={!hasReadAccess}>
                    <Icon name="cogs" />
                </Button>
                {isRunning ? (
                    <Button size="sm" color="warning" onClick={this.stopJob} disabled={this._store.isStopping || !hasRunAccess}>
                        <span>{this._store.isStopping ? 'Stopping..' : 'Stop now'}</span>
                    </Button>
                ) : (
                    <Button size="sm" color="success" onClick={this.runJob} disabled={!hasRunAccess}>
                        <span>Run now</span>
                    </Button>
                )}
            </ButtonGroup>
        );
    }

    renderTriggerProgress() {
        const triggers = this._triggers;

        if (!triggers.length) return this._renderJobLastNextRun();

        const failedFirst = [
            ...triggers.filter(isTriggerFailed),
            ...triggers.filter(t => !isTriggerFailed(t))
        ];
        const { showAllTriggers, toggleShowAllTriggers } = this._store;
        const visibleTriggers = showAllTriggers ? failedFirst : failedFirst.slice(0, COLLAPSED_TRIGGERS_COUNT);
        const hiddenCount = failedFirst.length - COLLAPSED_TRIGGERS_COUNT;

        return (
            <div className="mt-3">
                {visibleTriggers.map(this._renderTriggerRow)}
                {hiddenCount > 0 && (
                    <Button color="link" size="sm" className="trigger-rows-toggle" onClick={toggleShowAllTriggers}>
                        <Icon name={showAllTriggers ? 'chevron-up' : 'chevron-down'} />
                        {showAllTriggers ? 'Show less' : `Show all triggers (+${hiddenCount})`}
                    </Button>
                )}
            </div>
        );
    }

    private _renderJobLastNextRun() {
        const { lastRunDateTimeUtc, nextRunDateTimeUtc, lastRunTriggerName, nextRunTriggerName } = this.props.job;
        const lastRun = ((lastRunDateTimeUtc && DateTimeService.toUiDateTime(lastRunDateTimeUtc)) || 'never') + (lastRunTriggerName ? ` - ${lastRunTriggerName}` : '');
        const nextRun = ((nextRunDateTimeUtc && DateTimeService.toUiDateTime(nextRunDateTimeUtc)) || 'never') + (nextRunTriggerName ? ` - ${nextRunTriggerName}` : '');

        return (
            <div className="mt-3">
                <div className="job-details">Last run: {lastRun} | Next run: {nextRun}</div>
            </div>
        );
    }

    private _renderTriggerRow = (trigger: TriggerExtended) => {
        const triggerName = trigger.name ?? '';

        if (trigger.status === JobStatus.Pending) {
            return (
                <div key={triggerName} className="trigger-row">
                    <div className="trigger-main-line">
                        <span className="trigger-name-link" onClick={() => this.showJobParamsByName(triggerName)}>{triggerName}</span>
                        <Progress striped={true} animated={true} color={trigger.currentMessage === 'complete' ? 'success' : ''} max={trigger.progressMax ? trigger.progressMax : 1} value={trigger.progressCurrent ? trigger.progressCurrent : 1} className="progress-center-wrapper">
                            <span className="progress-center">{trigger.currentMessage}</span>
                        </Progress>
                    </div>
                </div>
            );
        }

        const lastRun = (trigger.lastRunDateTime && DateTimeService.toUiDateTime(trigger.lastRunDateTime)) || 'never';
        const nextRun = (trigger.nextRunDateTime && DateTimeService.toUiDateTime(trigger.nextRunDateTime)) || 'never';
        const isInactive = !trigger.isActive || !trigger.nextRunDateTime;
        const schedule = trigger.cronSchedule
            ? `Schedule: ${trigger.cronSchedule}`
            : trigger.dateTimeSchedule
                ? `Once: ${DateTimeService.toUiDateTime(trigger.dateTimeSchedule)}`
                : null;

        return (
            <div key={triggerName} className={`trigger-row${isInactive ? ' trigger-inactive' : ''}`}>
                <div className="trigger-main-line">
                    <span className="trigger-name-link" onClick={() => this.showJobParamsByName(triggerName)}>{triggerName}</span>
                    <span className="job-details">
                        {schedule && <>{schedule}{' | '}</>}
                        MisfireLogic: {trigger.misfireLogic}
                        {trigger.useZrhTimeZone && <span className="trigger-zrh" title="Schedule uses Zurich (ZRH) time zone">ZRH</span>}
                        {' | '}Last run: {lastRun} | Next run: {nextRun}
                    </span>
                </div>
                {trigger.scheduleError && (
                    <div className="trigger-error">
                        <Icon name="exclamation-triangle" />{' '}{trigger.scheduleError}
                    </div>
                )}
            </div>
        );
    };

    renderTriggerError() {
        const errorMessage = getJobErrorMessage(this.props.job);
        if (!errorMessage) return null;

        return (
            <Collapse isOpen={this._store.errorToggle} className="mt-3">
                <Alert color="danger">
                    {errorMessage.split('\n').map((x, index) => <div key={index}>{x}</div>)}
                </Alert>
            </Collapse>
        );
    }

    renderLink(description: string) {
        return ReactService.replaceString(description, LINK_REGEX, (s) => (
            <a key="{name}" href={s.replace('{site-root}', '')}>
                {s.replace('{site-root}', '')}
            </a>
        ));
    }

    private _renderQueues() {
        return (
            <>
                {this.props.queues.map(x =>
                    <SelfTestQueue key={x.id} queueName={x.name || ''} />
                )}
            </>
        );
    }

    toggleLastError = (event: React.MouseEvent<HTMLSpanElement>) => {
        const isError = event.currentTarget.id.indexOf('error_message') > -1;
        isError && this._store.toggleLastError();
    };

    runJob = () => this._store.runJob();

    stopJob = () => this._store.stopJob();

    showJobParamsByName = (name: string) => void this._store.showJobParams(name);

    showNewTriggerJobParams = () => this._store.showJobParams();

    showJobSettings = () => this._store.showJobSettings();
}
